/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  Stack,
  Flex,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import { fetchBreeds } from '../../services/api';
import SearchableSelect from '../SearchableSelect';

function Step2Details({ form, setForm, showErrors = false }) {
  const [breeds, setBreeds] = useState({ cats: [], dogs: [] });

  // Fetch breeds on component mount
  useEffect(() => {
    const loadBreeds = async () => {
      try {
        const breedsData = await fetchBreeds();
        setBreeds(breedsData);
      } catch (error) {
        console.error('Failed to load breeds:', error);
        // Keep empty arrays as fallback
        setBreeds({ cats: [], dogs: [] });
      }
    };

    loadBreeds();
  }, []);

  // Helper function to update pet information
  const updatePet = (index, field, value) => {
    const updatedPets = [...(form.pets || [])];
    if (!updatedPets[index]) {
      updatedPets[index] = { animalName: '', reason: '', species: '', breed: '' };
    }
    updatedPets[index][field] = value;
    setForm((f) => ({ ...f, pets: updatedPets }));
  };

  // Helper function to get pet data
  const getPet = (index) => {
    return form.pets?.[index] || { animalName: '', reason: '', species: '', breed: '' };
  };

  return (
    <Box maxW="100%" overflow="hidden">
      <Heading size="md" mb={{ base: 4, md: 6 }}>Pet Details</Heading>

      {/* Are you a returning client? */}
      <Box mb="6">
        <FormControl isRequired isInvalid={showErrors && !form.returningClient}>
          <FormLabel fontWeight="600" mb="3">Are you a returning client?</FormLabel>
          <Flex flexWrap="wrap" gap="3">
            <Button
              variant={form.returningClient === 'new' ? 'solid' : 'outline'}
              bg={form.returningClient === 'new' ? '#04385D' : 'transparent'}
              color={form.returningClient === 'new' ? 'white' : '#04385D'}
              borderColor="#04385D"
              borderWidth={form.returningClient === 'new' ? 0 : '1px'}
              _hover={{ bg: form.returningClient === 'new' ? '#032C47' : 'rgba(4,56,93,0.08)' }}
              onClick={() => setForm((f) => ({ ...f, returningClient: 'new' }))}
              minH="44px"
              flex={{ base: '1 1 100%', sm: '0 1 auto' }}
            >
              I am a new client
            </Button>
            <Button
              variant={form.returningClient === 'returning' ? 'solid' : 'outline'}
              bg={form.returningClient === 'returning' ? '#04385D' : 'transparent'}
              color={form.returningClient === 'returning' ? 'white' : '#04385D'}
              borderColor="#04385D"
              borderWidth={form.returningClient === 'returning' ? 0 : '1px'}
              _hover={{ bg: form.returningClient === 'returning' ? '#032C47' : 'rgba(4,56,93,0.08)' }}
              onClick={() => setForm((f) => ({ ...f, returningClient: 'returning' }))}
              minH="44px"
              flex={{ base: '1 1 100%', sm: '0 1 auto' }}
            >
              I am a returning client
            </Button>
          </Flex>
          <FormErrorMessage>Please select new or returning client</FormErrorMessage>
        </FormControl>
      </Box>

      {/* Number of pets */}
      <Box mb="6">
        <FormControl isRequired isInvalid={showErrors && !form.numberOfPets}>
          <FormLabel fontWeight="600" mb="3">How many pets are you making an appointment for?</FormLabel>
          <Flex flexWrap="wrap" gap="3">
            <Button
              variant={form.numberOfPets === '1' ? 'solid' : 'outline'}
              bg={form.numberOfPets === '1' ? '#04385D' : 'transparent'}
              color={form.numberOfPets === '1' ? 'white' : '#04385D'}
              borderColor="#04385D"
              borderWidth={form.numberOfPets === '1' ? 0 : '1px'}
              _hover={{ bg: form.numberOfPets === '1' ? '#032C47' : 'rgba(4,56,93,0.08)' }}
              onClick={() => setForm((f) => ({ ...f, numberOfPets: '1' }))}
              minH="44px"
              flex={{ base: '1 1 calc(50% - 6px)', sm: '0 1 auto' }}
            >
              1 Pet
            </Button>
            <Button
              variant={form.numberOfPets === '2' ? 'solid' : 'outline'}
              bg={form.numberOfPets === '2' ? '#04385D' : 'transparent'}
              color={form.numberOfPets === '2' ? 'white' : '#04385D'}
              borderColor="#04385D"
              borderWidth={form.numberOfPets === '2' ? 0 : '1px'}
              _hover={{ bg: form.numberOfPets === '2' ? '#032C47' : 'rgba(4,56,93,0.08)' }}
              onClick={() => setForm((f) => ({ ...f, numberOfPets: '2' }))}
              minH="44px"
              flex={{ base: '1 1 calc(50% - 6px)', sm: '0 1 auto' }}
            >
              2 Pets
            </Button>
            <Button
              variant={form.numberOfPets === '3' ? 'solid' : 'outline'}
              bg={form.numberOfPets === '3' ? '#04385D' : 'transparent'}
              color={form.numberOfPets === '3' ? 'white' : '#04385D'}
              borderColor="#04385D"
              borderWidth={form.numberOfPets === '3' ? 0 : '1px'}
              _hover={{ bg: form.numberOfPets === '3' ? '#032C47' : 'rgba(4,56,93,0.08)' }}
              onClick={() => setForm((f) => ({ ...f, numberOfPets: '3' }))}
              minH="44px"
              flex={{ base: '1 1 100%', sm: '0 1 auto' }}
            >
              3 Pets
            </Button>
          </Flex>
          <FormErrorMessage>Please select number of pets</FormErrorMessage>
        </FormControl>
      </Box>

      {/* Pet Details */}
      {form.numberOfPets && (
        <Box>
          <Text fontWeight="600" mb="4">Pet Information</Text>
          <Stack spacing="4">
            {Array.from({ length: parseInt(form.numberOfPets) }, (_, index) => (
              <Box
                key={index}
                border="1px"
                borderColor="gray.200"
                borderRadius="lg"
                p="3"
                bg="gray.50"
              >
                <Text fontWeight="600" fontSize="lg" mb="3" color="#04385D">
                  Pet {index + 1}
                </Text>

                {/* Animal Name and Reason */}
                <Box mb="3">
                  <Box mb="3">
                    <FormControl isRequired isInvalid={showErrors && !getPet(index).animalName}>
                      <FormLabel fontWeight="500" mb="2" fontSize="sm" color="gray.700">
                        Animal Name
                      </FormLabel>
                      <Input
                        placeholder="Enter pet's name *"
                        value={getPet(index).animalName}
                        onChange={(e) => updatePet(index, 'animalName', e.target.value)}
                        bg="white"
                      />
                      <FormErrorMessage>Pet name is required</FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl isRequired isInvalid={showErrors && !getPet(index).reason}>
                      <FormLabel fontWeight="500" mb="2" fontSize="sm" color="gray.700">
                        Reason for Visit
                      </FormLabel>
                      <Input
                        placeholder="E.g., Checkup, vaccination, illness... *"
                        value={getPet(index).reason}
                        onChange={(e) => updatePet(index, 'reason', e.target.value)}
                        bg="white"
                      />
                      <FormErrorMessage>Reason for visit is required</FormErrorMessage>
                    </FormControl>
                  </Box>
                </Box>

                {/* Species Selection */}
                <Box mb="3">
                  <FormControl isRequired isInvalid={showErrors && !getPet(index).species}>
                    <FormLabel fontWeight="500" mb="3" fontSize="sm" color="gray.700">
                      Species
                    </FormLabel>
                    <Flex flexWrap="wrap" gap="3">
                      <Button
                        variant={getPet(index).species === 'Dog' ? 'solid' : 'outline'}
                        bg={getPet(index).species === 'Dog' ? '#04385D' : 'transparent'}
                        color={getPet(index).species === 'Dog' ? 'white' : '#04385D'}
                        borderColor="#04385D"
                        borderWidth={getPet(index).species === 'Dog' ? 0 : '1px'}
                        _hover={{ bg: getPet(index).species === 'Dog' ? '#032C47' : 'rgba(4,56,93,0.08)' }}
                        onClick={() => updatePet(index, 'species', 'Dog')}
                        px={{ base: 4, md: 6 }}
                        minH="44px"
                        flex={{ base: '1 1 calc(50% - 6px)', md: '0 1 auto' }}
                      >
                        🐕 Dog
                      </Button>
                      <Button
                        variant={getPet(index).species === 'Cat' ? 'solid' : 'outline'}
                        bg={getPet(index).species === 'Cat' ? '#04385D' : 'transparent'}
                        color={getPet(index).species === 'Cat' ? 'white' : '#04385D'}
                        borderColor="#04385D"
                        borderWidth={getPet(index).species === 'Cat' ? 0 : '1px'}
                        _hover={{ bg: getPet(index).species === 'Cat' ? '#032C47' : 'rgba(4,56,93,0.08)' }}
                        onClick={() => updatePet(index, 'species', 'Cat')}
                        px={{ base: 4, md: 6 }}
                        minH="44px"
                        flex={{ base: '1 1 calc(50% - 6px)', md: '0 1 auto' }}
                      >
                        🐱 Cat
                      </Button>
                      <Button
                        variant={getPet(index).species === 'Others' ? 'solid' : 'outline'}
                        bg={getPet(index).species === 'Others' ? '#04385D' : 'transparent'}
                        color={getPet(index).species === 'Others' ? 'white' : '#04385D'}
                        borderColor="#04385D"
                        borderWidth={getPet(index).species === 'Others' ? 0 : '1px'}
                        _hover={{ bg: getPet(index).species === 'Others' ? '#032C47' : 'rgba(4,56,93,0.08)' }}
                        onClick={() => updatePet(index, 'species', 'Others')}
                        px={{ base: 4, md: 6 }}
                        minH="44px"
                        flex={{ base: '1 1 100%', md: '0 1 auto' }}
                      >
                        🐾 Others
                      </Button>
                    </Flex>
                    <FormErrorMessage>Please select a species</FormErrorMessage>
                  </FormControl>
                </Box>

                {/* Breed Field */}
                <Box>
                  <FormControl isRequired isInvalid={showErrors && !getPet(index).breed}>
                    <FormLabel fontWeight="500" mb="2" fontSize="sm" color="gray.700">
                      {getPet(index).species === 'Others' ? 'Species/Breed' : `${getPet(index).species} Breed`}
                    </FormLabel>
                    {getPet(index).species === 'Dog' || getPet(index).species === 'Cat' ? (
                      <SearchableSelect
                        options={getPet(index).species === 'Dog' ? breeds.dogs : breeds.cats}
                        placeholder={`Select ${getPet(index).species.toLowerCase()} breed`}
                        value={getPet(index).breed}
                        onChange={(value) => updatePet(index, 'breed', value)}
                      />
                    ) : (
                      <Input
                        placeholder="Enter species/breed (e.g., Rabbit, Bird, etc.)"
                        value={getPet(index).breed}
                        onChange={(e) => updatePet(index, 'breed', e.target.value)}
                        bg="white"
                        maxW="100%"
                      />
                    )}
                    <FormErrorMessage>Breed is required</FormErrorMessage>
                  </FormControl>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export default Step2Details;

